import { WEB_PUBLIC_ROOT_ROUTE_SEGMENTS } from '../identity/reserved-usernames';

export const CREATOR_SITE_HOMEPAGE_SLUG = 'home';
export const CREATOR_SITE_PAGE_SLUG_MAX = 64;
export const CREATOR_SITE_SUBPAGES_MAX = 10;
export const CREATOR_SITE_SECTIONS_MAX = 40;
export const CREATOR_SITE_THEME_PRESET_MAX = 64;

export const CREATOR_SITE_STATUSES = ['draft', 'published'] as const;
export type CreatorSiteStatus = (typeof CREATOR_SITE_STATUSES)[number];

export const CREATOR_SITE_SECTION_TYPES = [
  'hero',
  'navbar',
  'footer',
  'about',
  'rich_text',
  'faq',
  'testimonials',
  'case_study',
  'social_links',
  'link_row',
  'follow_loamify',
  'featured_offer',
  'offer_grid',
  'course_showcase',
  'membership_card',
  'event_list',
  'article_list',
  'featured_article',
  'newsletter_signup',
  'contact_form',
  'embed',
  'event_list',
  'image_gallery',
  'logo_strip',
  'stats',
  'spacer',
] as const;

export type CreatorSiteSectionType = (typeof CREATOR_SITE_SECTION_TYPES)[number];

export const CREATOR_SITE_THEME_PRESETS = [
  'minimal',
  'bold',
  'editorial',
  'coach',
  'creator',
  'dark',
] as const;

export type CreatorSiteThemePreset = (typeof CREATOR_SITE_THEME_PRESETS)[number];

export const CREATOR_SITE_THEME_TOKEN_KEYS = [
  'primary',
  'background',
  'surface',
  'text',
  'radius',
  'buttonStyle',
] as const;

export type CreatorSiteThemeTokenKey = (typeof CREATOR_SITE_THEME_TOKEN_KEYS)[number];

export type CreatorSiteThemeTokens = Partial<Record<CreatorSiteThemeTokenKey, string>>;

const RESERVED_PAGE_SLUGS = new Set<string>([
  ...WEB_PUBLIC_ROOT_ROUTE_SEGMENTS,
  CREATOR_SITE_HOMEPAGE_SLUG,
  'home',
  'index',
  'www',
  'api',
  'admin',
  'checkout',
  'cart',
  'sitemap',
  'robots',
]);

export function isCreatorSiteStatus(value: string): value is CreatorSiteStatus {
  return (CREATOR_SITE_STATUSES as readonly string[]).includes(value);
}

export function isCreatorSiteSectionType(value: string): value is CreatorSiteSectionType {
  return (CREATOR_SITE_SECTION_TYPES as readonly string[]).includes(value);
}

export function isCreatorSiteThemePreset(value: string): value is CreatorSiteThemePreset {
  return (CREATOR_SITE_THEME_PRESETS as readonly string[]).includes(value);
}

export function normalizeCreatorSitePageSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, CREATOR_SITE_PAGE_SLUG_MAX);
}

export function validateCreatorSitePageSlug(
  slug: string,
  options?: { isHomepage?: boolean },
): { ok: true; slug: string } | { ok: false; message: string } {
  if (options?.isHomepage) {
    if (slug !== CREATOR_SITE_HOMEPAGE_SLUG) {
      return { ok: false, message: 'Homepage slug must be "home"' };
    }
    return { ok: true, slug };
  }

  const normalized = normalizeCreatorSitePageSlug(slug);
  if (!normalized) {
    return { ok: false, message: 'Page slug is required' };
  }
  if (normalized.length < 2) {
    return { ok: false, message: 'Page slug must be at least 2 characters' };
  }
  if (RESERVED_PAGE_SLUGS.has(normalized)) {
    return { ok: false, message: 'Page slug is reserved' };
  }
  return { ok: true, slug: normalized };
}

export function validateCreatorSiteSubpageCount(subpageCount: number): { ok: true } | { ok: false; message: string } {
  if (subpageCount >= CREATOR_SITE_SUBPAGES_MAX) {
    return {
      ok: false,
      message: `Cannot exceed ${CREATOR_SITE_SUBPAGES_MAX} subpages`,
    };
  }
  return { ok: true };
}

export function validateCreatorSiteThemePreset(
  preset: string,
): { ok: true; preset: CreatorSiteThemePreset } | { ok: false; message: string } {
  if (!isCreatorSiteThemePreset(preset)) {
    return { ok: false, message: 'Invalid theme preset' };
  }
  return { ok: true, preset };
}

export function validateCreatorSiteThemeTokens(
  tokens: CreatorSiteThemeTokens | null | undefined,
): { ok: true; tokens: CreatorSiteThemeTokens | null } | { ok: false; message: string } {
  if (!tokens) {
    return { ok: true, tokens: null };
  }

  for (const key of Object.keys(tokens)) {
    if (!(CREATOR_SITE_THEME_TOKEN_KEYS as readonly string[]).includes(key)) {
      return { ok: false, message: `Unknown theme token: ${key}` };
    }
    const value = tokens[key as CreatorSiteThemeTokenKey];
    if (typeof value !== 'string' || !value.trim()) {
      return { ok: false, message: `Theme token ${key} must be a non-empty string` };
    }
    if (value.length > 128) {
      return { ok: false, message: `Theme token ${key} is too long` };
    }
  }

  return { ok: true, tokens };
}

export function canTransitionCreatorSiteStatus(
  current: CreatorSiteStatus,
  next: CreatorSiteStatus,
): boolean {
  if (current === next) return true;
  if (next === 'draft') return true;
  if (next === 'published') return current === 'draft' || current === 'published';
  return false;
}
